import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { discountAPI } from '../lib/api';
import { toast } from 'sonner';
import { formatPrice } from '../lib/utils';
import { 
  Percent, 
  Tag, 
  Plus, 
  Trash2, 
  Calendar,
  Hash,
  Euro,
  Copy,
  Check
} from 'lucide-react';

export default function DiscountCodesSection() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  
  const [newCode, setNewCode] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    min_order: 0,
    max_uses: null,
    valid_from: '',
    valid_until: ''
  });

  useEffect(() => {
    loadCodes();
  }, []);

  const loadCodes = async () => {
    try {
      setLoading(true);
      const res = await discountAPI.list();
      setCodes(res.data || []);
    } catch (error) {
      console.error('Errore caricamento codici sconto:', error);
      toast.error('Errore nel caricamento dei codici sconto');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCode = async () => {
    if (!newCode.code.trim()) {
      toast.error('Inserisci un codice');
      return;
    }

    try {
      const payload = {
        ...newCode,
        code: newCode.code.toUpperCase(),
        max_uses: newCode.max_uses ? parseInt(newCode.max_uses) : null,
        valid_from: newCode.valid_from || null,
        valid_until: newCode.valid_until || null
      };
      
      await discountAPI.create(payload);
      toast.success('Codice sconto creato');
      setDialogOpen(false);
      setNewCode({
        code: '',
        discount_type: 'percentage',
        discount_value: 10,
        min_order: 0,
        max_uses: null,
        valid_from: '',
        valid_until: ''
      });
      loadCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore creazione codice');
    }
  };

  const handleDeleteCode = async (codeId) => {
    if (!window.confirm('Eliminare questo codice sconto?')) return;
    
    try {
      await discountAPI.delete(codeId);
      toast.success('Codice eliminato');
      loadCodes();
    } catch (error) {
      toast.error('Errore eliminazione codice');
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
    toast.success('Codice copiato!');
  };

  const generateRandomCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewCode({ ...newCode, code: result });
  };

  const isCodeExpired = (code) => {
    if (!code.valid_until) return false;
    return new Date(code.valid_until) < new Date();
  };

  const isCodeActive = (code) => {
    const now = new Date();
    if (code.valid_from && new Date(code.valid_from) > now) return false;
    if (code.valid_until && new Date(code.valid_until) < now) return false;
    if (code.max_uses && code.uses_count >= code.max_uses) return false;
    return true;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  return (
    <div className="space-y-6">
      {/* Header with Add Button */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5" />
              Codici Sconto
            </CardTitle>
            <CardDescription>
              Crea e gestisci codici promozionali per i tuoi clienti
            </CardDescription>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-discount-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Codice
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Crea Codice Sconto</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Codice</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={newCode.code}
                      onChange={(e) => setNewCode({...newCode, code: e.target.value.toUpperCase()})}
                      placeholder="es. ESTATE2025"
                      className="uppercase"
                      data-testid="discount-code-input"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={generateRandomCode}
                      title="Genera codice casuale"
                    >
                      <Hash className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tipo Sconto</Label>
                    <Select 
                      value={newCode.discount_type} 
                      onValueChange={(v) => setNewCode({...newCode, discount_type: v})}
                    >
                      <SelectTrigger data-testid="discount-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentuale (%)</SelectItem>
                        <SelectItem value="fixed">Fisso (€)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Valore</Label>
                    <div className="relative">
                      <Input 
                        type="number"
                        min="0"
                        step={newCode.discount_type === 'percentage' ? '1' : '0.01'}
                        value={newCode.discount_value}
                        onChange={(e) => setNewCode({...newCode, discount_value: parseFloat(e.target.value) || 0})}
                        data-testid="discount-value-input"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        {newCode.discount_type === 'percentage' ? '%' : '€'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Ordine Minimo (€)</Label>
                    <Input 
                      type="number"
                      min="0"
                      step="0.01"
                      value={newCode.min_order}
                      onChange={(e) => setNewCode({...newCode, min_order: parseFloat(e.target.value) || 0})}
                      data-testid="min-order-input"
                    />
                  </div>
                  <div>
                    <Label>Utilizzi Max</Label>
                    <Input 
                      type="number"
                      min="0"
                      placeholder="Illimitati"
                      value={newCode.max_uses || ''}
                      onChange={(e) => setNewCode({...newCode, max_uses: e.target.value ? parseInt(e.target.value) : null})}
                      data-testid="max-uses-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Valido dal</Label>
                    <Input 
                      type="date"
                      value={newCode.valid_from}
                      onChange={(e) => setNewCode({...newCode, valid_from: e.target.value})}
                      data-testid="valid-from-input"
                    />
                  </div>
                  <div>
                    <Label>Valido fino al</Label>
                    <Input 
                      type="date"
                      value={newCode.valid_until}
                      onChange={(e) => setNewCode({...newCode, valid_until: e.target.value})}
                      data-testid="valid-until-input"
                    />
                  </div>
                </div>

                <Button 
                  onClick={handleCreateCode} 
                  className="w-full"
                  data-testid="save-discount-btn"
                >
                  Crea Codice Sconto
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="spinner" />
            </div>
          ) : codes.length === 0 ? (
            <div className="text-center py-12">
              <Tag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                Nessun codice sconto creato
              </p>
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Crea il primo codice
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {codes.map((code) => (
                <Card 
                  key={code.id} 
                  className={`${!isCodeActive(code) ? 'opacity-60' : ''}`}
                  data-testid={`discount-card-${code.code}`}
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                          {code.discount_type === 'percentage' ? (
                            <Percent className="w-6 h-6 text-primary" />
                          ) : (
                            <Euro className="w-6 h-6 text-primary" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-mono font-bold text-lg">{code.code}</p>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => handleCopyCode(code.code)}
                            >
                              {copiedCode === code.code ? (
                                <Check className="w-3 h-3 text-green-500" />
                              ) : (
                                <Copy className="w-3 h-3" />
                              )}
                            </Button>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {code.discount_type === 'percentage' 
                              ? `${code.discount_value}% di sconto`
                              : `${formatPrice(code.discount_value)} di sconto`
                            }
                            {code.min_order > 0 && ` • Min. ${formatPrice(code.min_order)}`}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="flex flex-col items-end gap-1">
                          <div className="flex items-center gap-2">
                            {isCodeActive(code) ? (
                              <Badge className="bg-green-500">Attivo</Badge>
                            ) : isCodeExpired(code) ? (
                              <Badge variant="destructive">Scaduto</Badge>
                            ) : (
                              <Badge variant="secondary">Non attivo</Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Usato: {code.uses_count || 0}
                            {code.max_uses && ` / ${code.max_uses}`}
                          </p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteCode(code.id)}
                          data-testid={`delete-discount-${code.code}`}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>

                    {(code.valid_from || code.valid_until) && (
                      <div className="mt-3 pt-3 border-t flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        {code.valid_from && code.valid_until ? (
                          <span>Valido dal {formatDate(code.valid_from)} al {formatDate(code.valid_until)}</span>
                        ) : code.valid_from ? (
                          <span>Valido dal {formatDate(code.valid_from)}</span>
                        ) : (
                          <span>Valido fino al {formatDate(code.valid_until)}</span>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Summary */}
      {codes.length > 0 && (
        <div className="grid sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Codici Attivi</p>
                  <p className="text-2xl font-bold text-green-600">
                    {codes.filter(c => isCodeActive(c)).length}
                  </p>
                </div>
                <Tag className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Totale Utilizzi</p>
                  <p className="text-2xl font-bold">
                    {codes.reduce((sum, c) => sum + (c.uses_count || 0), 0)}
                  </p>
                </div>
                <Hash className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Scaduti</p>
                  <p className="text-2xl font-bold text-red-600">
                    {codes.filter(c => isCodeExpired(c)).length}
                  </p>
                </div>
                <Calendar className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
